import axios from "axios";
import { format } from "date-fns";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";

import { getNonPersistentStore } from "../../stores";
const sharedModule = require("../../shared.js").default;
const baseUrl = require("../../shared.js").default.apiBaseUrl;
const { photoStorageUrl, genreToColor } = sharedModule;

const formatAMPM = (date) => {
  var hours = date.getHours();
  var minutes = date.getMinutes();
  var ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  let hoursString = `${hours}`;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  var strTime = hoursString + ":" + minutes + " " + ampm.toUpperCase();
  return strTime;
};

const formatDate = (date) => {
  return date ? format(new Date(date), "MMMM do") : "";
};

const formatDateWithDayOfWeek = (date) => {
  return date ? format(new Date(date), "EEEE, MMMM do") : "";
};

const convertUTCDateToLocalDate = (dateString) => {
  const date = new Date(dateString);
  return date;
};

const uploadFileToGCP = async (path, file, contentType) => {
  const storage = getStorage();
  const ref1 = ref(storage, path);
  const uploadTask = uploadBytesResumable(ref1, file, {
    contentType: contentType ?? "auto",
  });
  const promise = new Promise((resolve, reject) => {
    uploadTask.on("state_changed", (snapshot) => {
      const { bytesTransferred, totalBytes } = snapshot;
      const progress = (bytesTransferred / totalBytes) * 100;
      const nonPersistentStore = getNonPersistentStore();
      nonPersistentStore.setImageUploadProgress(progress);
      if (progress === 100) {
        resolve(true);
      }
    });
    uploadTask.catch((err) => {
      console.log(err);
      reject(false);
    });
  });
  return promise;
};

const getUrlForFilePath = (path) => {
  if (!path || path.length === 0) return "";
  if (path.startsWith("http")) return path;
  return `${photoStorageUrl}/${path}`;
};

const openInNewTab = (url) => {
  if (!url || url.length === 0) return;
  window.open(url, "_blank");
};

const goBack = (router) => {
  if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push("/search");
  }
};

const hasSongPassForGigInLocalCache = (gigId) => {
  const nonPersistentStore = getNonPersistentStore();
  const songPass = nonPersistentStore.localSongPasses.find((elem) => elem.gigId === gigId);
  if (songPass) {
    return true;
  }
  return false;
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const passTest = re.test(String(email).toLowerCase());
  return passTest;
};

const validatePhoneNumber = (phone) => {
  const re = /[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]/;
  const passTest = re.test(String(phone).toLowerCase());
  return passTest;
};

// assign gigs to cards
const isToday = (someDate) => {
  const today = new Date();
  return (
    someDate.getDate() == today.getDate() &&
    someDate.getMonth() == today.getMonth() &&
    someDate.getFullYear() == today.getFullYear()
  );
};

const isActive = (gig) => {
  const today = new Date();
  const start = gig.startDate;
  const end = gig.endDate;
  if (start && end && start <= today && end >= today) {
    return true;
  } else {
    return false;
  }
};

const hashDate = (date) => {
  const copyDate = new Date(date);
  copyDate.setHours(0);
  copyDate.setMinutes(0);
  copyDate.setSeconds(0);
  return copyDate.toString();
};

const addMetadataToGigs = (gigs) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return gigs
    .sort((a, b) => a.startDate - b.startDate)
    .map((gig) => {
      const startDate = gig.startDate;
      const endDate = gig.endDate;
      gig.isToday = isToday(startDate) || isActive(gig);
      gig.dateHash = hashDate(startDate);
      gig.month = months[startDate.getMonth()].substring(0, 3).toUpperCase();
      gig.day = startDate.getDate();
      gig.startTime = formatAMPM(startDate);
      gig.endTime = formatAMPM(endDate);
      gig.formattedStart = formatDateWithDayOfWeek(startDate);
      gig.bandPhoto = getUrlForFilePath(gig.bandCoverPhoto);
      return gig;
    })
    .sort((a, b) => b.isToday - a.isToday);
};

const assignGigsToCards = (gigs) => {
  const metaDataGigs = addMetadataToGigs(gigs);
  const hashedDates = {};
  const hashedDatesObj = [];
  for (const gig of metaDataGigs) {
    let { dateHash, isToday } = gig;
    if (isToday) {
      dateHash = "today";
    }
    if (dateHash in hashedDates) {
      hashedDates[dateHash].push(gig);
    } else {
      hashedDates[dateHash] = [gig];
    }
  }
  for (const key in hashedDates) {
    const data = hashedDates[key];
    const gig = data[0];
    const { dateHash, isToday, startDate, formattedStart } = gig;
    hashedDatesObj.push({
      title: isToday ? "Today" : formattedStart,
      dateHash,
      isToday,
      startDate: startDate,
      gigs: data,
    });
  }
  return hashedDatesObj;
};

// format functions
const generalFormat = (obj) => {
  if (obj?.createdAt) {
    obj.createdAt = convertUTCDateToLocalDate(obj.createdAt);
  }
  if (obj?.updatedAt) {
    obj.updatedAt = convertUTCDateToLocalDate(obj.updatedAt);
  }
  return obj;
};

const formatBand = (band) => {
  band.landingPagePhotoUrl = band.landingPagePhotoUrl
    ? getUrlForFilePath(band.landingPagePhotoUrl)
    : null;
  band.coverPhoto = band.coverPhoto ? getUrlForFilePath(band.coverPhoto) : null;
  band.createdAt = band.createdAt ? convertUTCDateToLocalDate(band.createdAt) : null;
  band.socials = band.socials ? band.socials.sort((a, b) => a.orderNum - b.orderNum) : [];
  return band;
};

const formatGig = (gig) => {
  gig.startDate = gig.startDate ? convertUTCDateToLocalDate(gig.startDate) : null;
  gig.endDate = gig.endDate ? convertUTCDateToLocalDate(gig.endDate) : null;
  gig.displayDate = gig.displayDate ? convertUTCDateToLocalDate(gig.displayDate) : null;
  gig.presets = gig.presets ? JSON.parse(gig.presets.replaceAll("'", "")) : [];
  gig.freeModeThankYouText = gig.freeModeThankYouText ? gig.freeModeThankYouText : "";
  gig.hasLandingPage =
    gig.allowSongPasses ||
    gig.allowPhotoTextShoutouts ||
    gig.allowOnlyTextShoutouts ||
    gig.allowOnlyPhotoShoutouts ||
    gig.allowVideoShoutouts ||
    gig.hasExperiences ||
    gig.hasEvents ||
    gig.hasPhraseBoards ||
    gig.hasEventCompanion ||
    gig.hasSongBattles
      ? true
      : false;

  if (gig?.authId === 602) {
    gig.hasLandingPage = false;
  }
  const genres = gig.bandGenre ? gig.bandGenre.split(",") : [];
  gig.genreTags = genres
    .map((genre, index) => {
      if (genreToColor[genre]) {
        return {
          ID: "genreTag" + index,
          name: genre,
          color: genreToColor[genre],
          textColor: "#FFFFFF",
        };
      }
    })
    .filter((tag) => tag !== undefined);
  return gig;
};

const formatUser = (user) => {
  user.creationTime = user.creationTime ? convertUTCDateToLocalDate(user.creationTime) : null;
  return user;
};

const formatSong = (song) => {
  song.createdAt = song.createdAt ? convertUTCDateToLocalDate(song.createdAt) : null;
  if (song.minimum) {
    song.minimum = typeof song.minimum === "string" ? parseInt(song.minimum) : song.minimum;
  }
  return song;
};

const formatSetlist = (setlist) => {
  setlist.createdAt = setlist.createdAt ? convertUTCDateToLocalDate(setlist.createdAt) : null;
  setlist.songs = setlist.songs ? setlist.songs.map(formatSong) : [];
  return setlist;
};

const formatTip = (tip) => {
  tip.createdAt = tip.createdAt ? convertUTCDateToLocalDate(tip.createdAt) : null;
  if (tip.minimum) {
    tip.minimum = typeof tip.minimum === "string" ? parseInt(tip.minimum) : tip.minimum;
  }
  return tip;
};

const formatTipsGroupedForGig = (obj) => {
  obj.tips = obj.tips.map(formatTip);
  obj.requests = obj.requests.map((tip) => {
    tip.firstVoted = tip.firstVoted ? convertUTCDateToLocalDate(tip.firstVoted) : null;
    tip.lastVoted = tip.lastVoted ? convertUTCDateToLocalDate(tip.lastVoted) : null;
    tip.markedPlayedAt = tip.markedPlayedAt ? convertUTCDateToLocalDate(tip.markedPlayedAt) : null;
    if (tip.minimum) {
      tip.minimum = typeof tip.minimum === "string" ? parseInt(tip.minimum) : tip.minimum;
    }
    return tip;
  });
  return obj;
};

const formatEmailListItem = (emailList) => {
  emailList.createdAt = emailList.createdAt ? convertUTCDateToLocalDate(emailList.createdAt) : null;
  emailList.amount_earned = emailList.amount_earned ? emailList.amount_earned / 100.0 : 0;
  return emailList;
};

const formatFAQ = (faq) => {
  return faq;
};

const formatPayout = (payout) => {
  payout.createdAt = payout.createdAt ? convertUTCDateToLocalDate(payout.createdAt) : null;
  return payout;
};

const formatStage = (stage) => {
  return stage;
};

const formatVenue = (venue) => {
  if (venue.stages) {
    venue.stages = venue.stages.map(formatStage);
  }
  return venue;
};

const formatEventOption = (eventOption) => {
  return eventOption;
};

const formatEvent = (event) => {
  if (event.options) {
    event.options = event.options.map(formatEventOption);
  }
  return event;
};

const formatEventTransaction = (eventTransaction) => {
  eventTransaction.createdAt = eventTransaction.createdAt
    ? convertUTCDateToLocalDate(eventTransaction.createdAt)
    : null;
  return eventTransaction;
};

const formatPostShowSurvey = (postShowSurvey) => {
  return postShowSurvey;
};

const formatBandTip = (bandTip) => {
  bandTip.createdAt = bandTip.createdAt ? convertUTCDateToLocalDate(bandTip.createdAt) : null;
  return bandTip;
};

const formatCustomer = (customer) => {
  return customer;
};

const formatFollower = (follower) => {
  follower.createdAt = follower.createdAt ? convertUTCDateToLocalDate(follower.createdAt) : null;
  return follower;
};

const formatPost = (post) => {
  post.createdAt = post.createdAt ? convertUTCDateToLocalDate(post.createdAt) : null;
  return post;
};

const formatDynamicLink = (dynamicLink) => {
  dynamicLink.createdAt = dynamicLink.createdAt
    ? convertUTCDateToLocalDate(dynamicLink.createdAt)
    : null;
  dynamicLink.updatedAt = dynamicLink.updatedAt
    ? convertUTCDateToLocalDate(dynamicLink.updatedAt)
    : null;
  return dynamicLink;
};

const formatLiveForm = (liveForm) => {
  liveForm = generalFormat(liveForm);
  liveForm.imageUrl = getUrlForFilePath(liveForm.imageUrl);
  return liveForm;
};

const formatResetSong = (resetSong) => {
  return resetSong;
};

const formatSongPass = (songPass) => {
  songPass.createdAt = songPass.createdAt ? convertUTCDateToLocalDate(songPass.createdAt) : null;
  return songPass;
};

const formatShoutout = (shoutout) => {
  shoutout.createdAt = shoutout.createdAt ? convertUTCDateToLocalDate(shoutout.createdAt) : null;
  return shoutout;
};

const formatExperience = (experience) => {
  experience.createdAt = experience.createdAt
    ? convertUTCDateToLocalDate(experience.createdAt)
    : null;
  return experience;
};

const formatGigExperience = (gigExperience) => {
  gigExperience.startDate = gigExperience.startDate
    ? convertUTCDateToLocalDate(gigExperience.startDate)
    : null;
  return gigExperience;
};

const formatExperienceTransaction = (experienceTransaction) => {
  experienceTransaction.createdAt = experienceTransaction.createdAt
    ? convertUTCDateToLocalDate(experienceTransaction.createdAt)
    : null;
  experienceTransaction.paymentCollectedAt = experienceTransaction.paymentCollectedAt
    ? convertUTCDateToLocalDate(experienceTransaction.paymentCollectedAt)
    : null;
  return experienceTransaction;
};

const formatExperienceTransactionsGroupedForGig = (obj) => {
  if (obj) {
    const out = {};

    for (const key in obj) {
      if (key === "gigId") {
        out[key] = obj[key];
      } else {
        const objKeys = Object.keys(obj[key]);
        const inner = {};
        for (const objKey of objKeys) {
          inner[objKey] = obj[key][objKey].map(formatExperienceTransaction);
        }
        out[key] = inner;
      }
    }
    return out;
  } else {
    return null;
  }
};

const formatMerchandise = (merchandise) => {
  merchandise.createdAt = merchandise.createdAt
    ? convertUTCDateToLocalDate(merchandise.createdAt)
    : null;
  return merchandise;
};

const formatMerchandiseTransaction = (merchandiseTransaction) => {
  merchandiseTransaction.createdAt = merchandiseTransaction.createdAt
    ? convertUTCDateToLocalDate(merchandiseTransaction.createdAt)
    : null;
  return merchandiseTransaction;
};

const formatSongBattle = (songBattle) => {
  songBattle.createdAt = songBattle.createdAt
    ? convertUTCDateToLocalDate(songBattle.createdAt)
    : null;
  return songBattle;
};

const formatGigSongBattle = (gigSongBattle) => {
  gigSongBattle.createdAt = gigSongBattle.createdAt
    ? convertUTCDateToLocalDate(gigSongBattle.createdAt)
    : null;
  return gigSongBattle;
};

const formatSongBattleTransaction = (songBattleTransaction) => {
  songBattleTransaction.createdAt = songBattleTransaction.createdAt
    ? convertUTCDateToLocalDate(songBattleTransaction.createdAt)
    : null;
  return songBattleTransaction;
};

const getCurrencySymbol = (currency) => {
  if (!currency) return "$";
  const upper = currency.toUpperCase();
  const nonPersistentStore = getNonPersistentStore();
  const currencyOptions = nonPersistentStore.currencyOptions;
  if (!currencyOptions || !Array.isArray(currencyOptions)) return "$";
  const option = currencyOptions.find((option) => option.text === upper);
  return option?.symbol || "$";
};

const getCurrencyConversionRate = async (from, to) => {
  try {
    const endpoint = `${baseUrl}/currency/conversion-rate?from=${from}&to=${to}`;
    const response = await axios.get(endpoint);
    const axiosData = response.data;
    return axiosData;
  } catch (err) {
    console.log(err);
    return null;
  }
};

const convertCurrencyObjects = async (objects, objectKey, from, to) => {
  try {
    const endpoint = `${baseUrl}/currency/conversion-rate?from=${from}&to=${to}`;
    const response = await axios.get(endpoint);
    const axiosData = response.data;
    const { rate } = axiosData;
    const rateToDecimal = rate / 100.0;
    return objects.map((elem) => {
      elem[`${objectKey}_converted`] = Math.round(elem[objectKey] * rateToDecimal);
      return elem;
    });
  } catch (err) {
    console.log(err);
    return false;
  }
};

const checkIfStringIsInteger = (str) => {
  const regexCheck = new RegExp("^[0-9]+$");
  return regexCheck.test(str);
};

const checkIfStringIsDecimal = (str) => {
  const regexCheckForDecimal = new RegExp("^[0-9]+([.][0-9]+)?$");
  return regexCheckForDecimal.test(str);
};

const checkSpotifyAllowedUsers = (currentFirebaseUserId) => {
  return (
    currentFirebaseUserId === "8SljiC5UIxalsdllEw54awxueDh2" || // Tim
    currentFirebaseUserId === "On2jumwFSyPUsSHYKS40nbDNjwP2" || // Grif
    currentFirebaseUserId === "YeF1XHFGieckrFbkxCo3sHD20fw2" || // Kaydee
    currentFirebaseUserId === "EE0QNw7FgGV8aHfJHS4aXgdmbsg1" || // Jack
    currentFirebaseUserId === "NL2qIT82JQTKierr00e8Oewx2913" || // Taylor
    currentFirebaseUserId === "gtFKIOawC6fUd9OVA8G1W8CYti92" || // Tom K
    currentFirebaseUserId === "SCMRlcWyJURv9PSvcpVZE1J5cfG2" || // Chris
    currentFirebaseUserId === "NQOgV2BtCpeMsl8ZsIJJaLjvYNp2" || // Gretchen Connors
    currentFirebaseUserId === "VZKe6kqY2dVIcpCwA7slaGUZJkU2" || // testuser123@juke.band
    currentFirebaseUserId === "ZlfyOpWUrKX3v1FKk2rIrY800lE3" || // Taylor dev
    currentFirebaseUserId === "h8IxsqA4CeQZiiqNsNRAZ1B2U2F3" // Grif dev
  );
};

const createDateFromDatePicker = (newValue) => {
  if (!newValue || typeof newValue !== "string") {
    return null;
  }
  const split = newValue.split("-");
  if (split.length !== 3) {
    return null;
  }
  const year = parseInt(split[0]);
  const month = parseInt(split[1]);
  const day = parseInt(split[2]);
  // Create date using local timezone to avoid timezone conversion issues
  // Use Date constructor with year, month (0-indexed), day to ensure local timezone
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);
  return d;
};

export {
  // time formatting
  formatDate,
  formatAMPM,
  generalFormat,
  addMetadataToGigs,
  assignGigsToCards,
  // uploading files
  uploadFileToGCP,
  getUrlForFilePath,
  hasSongPassForGigInLocalCache,
  // formatting methods
  formatBand,
  formatGig,
  formatUser,
  formatSetlist,
  formatSong,
  formatTip,
  formatTipsGroupedForGig,
  formatEmailListItem,
  formatFAQ,
  formatPayout,
  formatVenue,
  formatStage,
  formatEventOption,
  formatEvent,
  formatEventTransaction,
  formatPostShowSurvey,
  formatBandTip,
  formatCustomer,
  formatResetSong,
  formatSongPass,
  formatShoutout,
  formatExperience,
  formatGigExperience,
  formatExperienceTransaction,
  formatExperienceTransactionsGroupedForGig,
  formatMerchandise,
  formatMerchandiseTransaction,
  formatSongBattle,
  formatSongBattleTransaction,
  formatGigSongBattle,
  formatFollower,
  formatPost,
  formatDynamicLink,
  formatLiveForm,
  // currency stuff
  getCurrencySymbol,
  convertCurrencyObjects,
  getCurrencyConversionRate,
  // random
  validateEmail,
  validatePhoneNumber,
  checkIfStringIsDecimal,
  checkIfStringIsInteger,
  checkSpotifyAllowedUsers,
  openInNewTab,
  goBack,
  createDateFromDatePicker,
  isToday,
};
